import { ElMessage, ElMessageBox } from "element-plus";
import { ApiError, FormDefinition } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { getMessageTime } from "~/utils/miscUtils";

/**
 * Draft lifecycle for in-use form definitions (issue 1014).
 *
 * Forms that are in use (have responses, or workflow instances that can still create
 * responses against them) are never edited directly: the designer edits a server-side
 * draft snapshot and releases all changes at once with Commit, or throws them away
 * with Discard. The server rejects direct saves to in-use forms with a 409.
 */

export interface EditableFormResolution {
  /** The definition the designer should edit (the draft when in draft mode). */
  form: FormDefinition;
  /** True when edits go to a draft instead of the live definition. */
  isDraftMode: boolean;
  /** Last-saved timestamp of the draft, for the commit concurrency check. */
  draftUpdatedAt?: string;
  /**
   * True when the resumed draft already has saved changes vs the live form (its
   * last-saved time differs from its creation time). False for a brand-new/pristine
   * draft. Undefined when not in draft mode.
   */
  draftHasSavedChanges?: boolean;
}

export interface DraftStatus {
  /** True when the form is in use, so editing must go through a draft. */
  requiresDraft: boolean;
  /** True when a draft already exists for the form. */
  hasActiveDraft: boolean;
}

/**
 * Reports whether a form needs a draft to edit and whether one already exists —
 * without creating a draft. Uses the flags on the passed form when present, else
 * fetches them.
 */
export async function getDraftStatus(
  form: FormDefinition,
): Promise<DraftStatus> {
  let requiresDraft = form.requiresDraft;
  let hasActiveDraft = form.hasActiveDraft;
  const flagsMissing =
    requiresDraft === undefined ||
    requiresDraft === null ||
    hasActiveDraft === undefined ||
    hasActiveDraft === null;
  if (flagsMissing) {
    const fresh = await apiClient.formDefinition.getFormDefinition(form.guid);
    requiresDraft = fresh.requiresDraft;
    hasActiveDraft = fresh.hasActiveDraft;
  }
  return {
    requiresDraft: !!requiresDraft,
    hasActiveDraft: !!hasActiveDraft,
  };
}

/**
 * Returns the definition the designer should edit: the form itself when it can be
 * edited directly, or its draft (created or resumed) when the form is in use. Always
 * creates/resumes a draft for in-use forms — call only when the user opts into editing.
 * @param options.silentResume suppress the "resumed draft" toast (used when auto-resuming).
 */
export async function resolveEditableForm(
  form: FormDefinition,
  options: { silentResume?: boolean } = {},
): Promise<EditableFormResolution> {
  const { requiresDraft, hasActiveDraft } = await getDraftStatus(form);

  if (!requiresDraft && !hasActiveDraft) {
    return { form, isDraftMode: false };
  }

  const result = await apiClient.formDefinition.createOrResumeDraft(form.guid);

  if (result.resumed && !options.silentResume) {
    const startedBy = result.createdBy ? ` by ${result.createdBy}` : "";
    const startedAt = result.createdAt
      ? ` on ${new Date(result.createdAt).toLocaleString()}`
      : "";
    const msg = `Resumed the existing draft started${startedBy}${startedAt}.`;
    ElMessage.info({
      message: msg,
      grouping: true,
      duration: getMessageTime(msg),
    });
  }

  return {
    form: result.draft,
    isDraftMode: true,
    draftUpdatedAt: result.draftUpdatedAt,
    // A pristine draft has updatedAt === createdAt; any saved edits move updatedAt.
    draftHasSavedChanges:
      !!result.createdAt && result.draftUpdatedAt !== result.createdAt,
  };
}

/** Shows the commit confirmation dialog. Resolves true if the user confirmed. */
export async function confirmCommitDraft(): Promise<boolean> {
  try {
    await ElMessageBox.confirm(
      "Publish this draft? The live form is replaced with the draft in one step. " +
        "Workflows and responses will see the new version immediately; removed " +
        "questions that already have answers are archived, not deleted.",
      "Commit Draft",
      {
        confirmButtonText: "Commit",
        cancelButtonText: "Keep Editing",
        type: "warning",
      },
    );
    return true;
  } catch {
    return false; // cancelled
  }
}

/**
 * Commits the form's draft (no confirmation — call confirmCommitDraft first). Returns
 * the committed live definition, or undefined on failure (errors are toasted here).
 */
export async function commitDraft(
  formDefinitionGuid: string,
  expectedDraftUpdatedAt?: string,
): Promise<FormDefinition | undefined> {
  try {
    const committed = await apiClient.formDefinition.commitDraft(
      formDefinitionGuid,
      expectedDraftUpdatedAt,
    );
    const msg = "Draft committed — the live form has been updated.";
    ElMessage.success({ message: msg, duration: getMessageTime(msg) });
    return committed;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      showError(
        "This draft no longer exists — another user committed or discarded it.",
        error,
      );
    } else if (error instanceof ApiError && error.status === 409) {
      showError(
        "The draft was saved by someone else after you loaded it. Reload before committing.",
        error,
      );
    } else {
      showError(error);
    }
    return undefined;
  }
}

/** Shows the discard confirmation dialog. Resolves true if the user confirmed. */
export async function confirmDiscardDraft(): Promise<boolean> {
  try {
    await ElMessageBox.confirm(
      "Discard this draft? All draft changes are lost; the live form is untouched.",
      "Discard Draft",
      {
        confirmButtonText: "Discard Draft",
        cancelButtonText: "Keep Editing",
        type: "warning",
      },
    );
    return true;
  } catch {
    return false; // cancelled
  }
}

/**
 * Discards the form's draft (no confirmation — call confirmDiscardDraft first). Returns
 * true when the draft was removed (errors are toasted here).
 */
export async function discardDraft(
  formDefinitionGuid: string,
): Promise<boolean> {
  try {
    await apiClient.formDefinition.discardDraft(formDefinitionGuid);
    const msg = "Draft discarded — the live form is unchanged.";
    ElMessage.success({ message: msg, duration: getMessageTime(msg) });
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      // Already gone (committed/discarded elsewhere) — treat as done
      return true;
    }
    showError(error);
    return false;
  }
}

/**
 * Best-effort cleanup when leaving the designer without ever saving: removes the
 * draft only if it is pristine (the server checks). Failures are ignored.
 */
export function discardDraftIfPristine(formDefinitionGuid: string): void {
  apiClient.formDefinition
    .discardDraft(formDefinitionGuid, true)
    .catch((err) => console.warn("Pristine draft cleanup failed", err));
}
